/**
 * CommandBar Component - Microsoft 365 Admin Console Style
 * Action bar with buttons for common operations
 */

import React from 'react';
import { Toolbar, ToolbarButton, ToolbarDivider, makeStyles } from '@fluentui/react-components';
import {
  AddRegular,
  FilterRegular,
  SearchRegular,
  ArrowDownloadRegular,
  ArrowUploadRegular,
  DeleteRegular,
  EditRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  toolbar: {
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
});

interface CommandBarAction {
  key: string;
  text: string;
  icon?: React.ReactElement;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

interface CommandBarProps {
  actions: CommandBarAction[];
}

export const CommandBar: React.FC<CommandBarProps> = ({ actions }) => {
  const styles = useStyles();

  const primaryActions = actions.filter(action => action.primary);
  const secondaryActions = actions.filter(action => !action.primary);

  return (
    <Toolbar className={styles.toolbar}>
      {/* Primary Actions */}
      {primaryActions.map((action, index) => (
        <React.Fragment key={action.key}>
          <ToolbarButton
            icon={action.icon}
            appearance="primary"
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {action.text}
          </ToolbarButton>
          {index < primaryActions.length - 1 && <ToolbarDivider />}
        </React.Fragment>
      ))}

      {primaryActions.length > 0 && secondaryActions.length > 0 && <ToolbarDivider />}

      {/* Secondary Actions */}
      {secondaryActions.map(action => (
        <ToolbarButton
          key={action.key}
          icon={action.icon}
          disabled={action.disabled}
          onClick={action.onClick}
        >
          {action.text}
        </ToolbarButton>
      ))}
    </Toolbar>
  );
};

/**
 * Pre-built CommandBar variants for common use cases
 */

const useTableCommandBarStyles = makeStyles({
  commandBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #edebe9',
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  actionsContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
});

interface TableCommandBarProps {
  title?: string;
  actions?: React.ReactNode;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onExport?: () => void;
  onImport?: () => void;
  onFilter?: () => void;
  onSearch?: () => void;
  addLabel?: string;
  disabled?: {
    add?: boolean;
    edit?: boolean;
    delete?: boolean;
    export?: boolean;
    import?: boolean;
  };
}

export const TableCommandBar: React.FC<TableCommandBarProps> = ({
  title,
  actions,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
  onFilter,
  onSearch,
  addLabel = 'Add New',
  disabled = {},
}) => {
  const styles = useTableCommandBarStyles();

  if (title || actions) {
    return (
      <div className={styles.commandBar}>
        {title && <div className={styles.title}>{title}</div>}
        {actions && <div className={styles.actionsContainer}>{actions}</div>}
      </div>
    );
  }

  const commandBarActions: CommandBarAction[] = [];

  if (onAdd) {
    commandBarActions.push({
      key: 'add',
      text: addLabel,
      icon: <AddRegular />,
      primary: true,
      disabled: disabled.add,
      onClick: onAdd,
    });
  }

  if (onEdit) {
    commandBarActions.push({
      key: 'edit',
      text: 'Edit',
      icon: <EditRegular />,
      disabled: disabled.edit,
      onClick: onEdit,
    });
  }

  if (onDelete) {
    commandBarActions.push({
      key: 'delete',
      text: 'Delete',
      icon: <DeleteRegular />,
      disabled: disabled.delete,
      onClick: onDelete,
    });
  }

  if (onExport) {
    commandBarActions.push({
      key: 'export',
      text: 'Export',
      icon: <ArrowDownloadRegular />,
      disabled: disabled.export,
      onClick: onExport,
    });
  }

  if (onImport) {
    commandBarActions.push({
      key: 'import',
      text: 'Import',
      icon: <ArrowUploadRegular />,
      disabled: disabled.import,
      onClick: onImport,
    });
  }

  if (onFilter) {
    commandBarActions.push({
      key: 'filter',
      text: 'Filter',
      icon: <FilterRegular />,
      onClick: onFilter,
    });
  }

  if (onSearch) {
    commandBarActions.push({
      key: 'search',
      text: 'Search',
      icon: <SearchRegular />,
      onClick: onSearch,
    });
  }

  return <CommandBar actions={commandBarActions} />;
};

/**
 * FormCommandBar - For forms with Save and Cancel
 */

interface FormCommandBarProps {
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  loading?: boolean;
}

export const FormCommandBar: React.FC<FormCommandBarProps> = ({
  onSave,
  onCancel,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  saveDisabled = false,
  loading = false,
}) => {
  const actions: CommandBarAction[] = [
    {
      key: 'save',
      text: loading ? 'Saving...' : saveLabel,
      primary: true,
      disabled: saveDisabled || loading,
      onClick: onSave,
    },
    {
      key: 'cancel',
      text: cancelLabel,
      disabled: loading,
      onClick: onCancel,
    },
  ];

  return <CommandBar actions={actions} />;
};
